import { findIndex, find, get, groupBy, sortBy, includes } from 'lodash';
import {
  getRandomBoolean,
  getScorepointShuffleType,
  shuffleArray,
  getCarriedForwardAnswers,
  getCarriedForwardScorepoints,
} from '../../../../../utils';
import { getGenericShuffleType } from '../../../../Answers/utils';
import {
  QUESTION_MATRIX_OPEN_ENDED_STYLES,
  QUESTION_MATRIX_OPEN_ENDED_INPUT_SIZES,
  QUESTION_MATRIX_OPEN_ENDED_SIGN_PLACEMENTS,
  QUESTION_MATRIX_OPEN_ENDED_DECIMALS_DEFAULT,
  QUESTION_MATRIX_OPEN_ENDED_MIN_VALUE_DEFAULT,
  QUESTION_MATRIX_OPEN_ENDED_MAX_VALUE_DEFAULT,
  QUESTION_MATRIX_OPEN_ENDED_CONSTANT_SUM_TOTAL_VALUE_DEFAULT,
  QUESTION_MATRIX_OPEN_ENDED_CONSTANT_SUM_TYPES,
} from '../constants';
import {
  QUESTION_MATRIX_OPEN_ENDED,
  QUESTION_MATRIX_OPEN_ENDED_CONSTANT_SUM,
} from '../../../../../constants';

export default function useSetup({ question }) {
  const { shuffle: shuffleType, style } = question;
  // shuffle the columns once,
  // only SHUFFLE_NONE and SHUFFLE_COLUMNS_REVERSE is allowed
  // we must apply the same shuffling for
  // columns, labels and rowLabels for each row
  const shouldReverseShuffle = getRandomBoolean();
  const columnsShuffleType = getScorepointShuffleType(shuffleType);
  const rowsShuffleType = getGenericShuffleType(question);

  const matrix = get(question, 'matrix', {});
  const config = get(matrix, 'openEndedConfig', {});
  const openEndedType = style || QUESTION_MATRIX_OPEN_ENDED;
  const openEndedConstantSumType =
    config.constantSumType ||
    QUESTION_MATRIX_OPEN_ENDED_CONSTANT_SUM_TYPES.MUST_TOTAL;
  const openEndedStyle =
    config.style || QUESTION_MATRIX_OPEN_ENDED_STYLES.HORIZONTAL;
  const openEndedSign = config.sign || '';
  let openEndedInputSize =
    config.inputSize || QUESTION_MATRIX_OPEN_ENDED_INPUT_SIZES.MEDIUM;
  let openEndedSignPlacement =
    config.signPlacement || QUESTION_MATRIX_OPEN_ENDED_SIGN_PLACEMENTS.RIGHT;

  if (openEndedStyle === QUESTION_MATRIX_OPEN_ENDED_STYLES.VERTICAL) {
    openEndedInputSize = QUESTION_MATRIX_OPEN_ENDED_INPUT_SIZES.FULL_WIDTH;
  }

  if (!openEndedSign) {
    openEndedSignPlacement = QUESTION_MATRIX_OPEN_ENDED_SIGN_PLACEMENTS.NONE;
  }

  const options = get(question, 'matrix.validation.openEndedValidation', {});
  const {
    min = QUESTION_MATRIX_OPEN_ENDED_MIN_VALUE_DEFAULT,
    max = QUESTION_MATRIX_OPEN_ENDED_MAX_VALUE_DEFAULT,
    decimals = QUESTION_MATRIX_OPEN_ENDED_DECIMALS_DEFAULT,
    total = QUESTION_MATRIX_OPEN_ENDED_CONSTANT_SUM_TOTAL_VALUE_DEFAULT,
  } = options;

  let rows = getCarriedForwardAnswers(question);
  rows = sortBy(rows, ['order']);

  let columns = getCarriedForwardScorepoints(question);
  columns = sortBy(columns, ['order']);
  if (shouldReverseShuffle) {
    columns = shuffleArray(columns, columnsShuffleType, {
      forceReverse: shouldReverseShuffle,
    });
  }

  let answerColumnValueTypes = matrix.answerColumnValueTypes || [];
  // we need to clean the answerColumnValueTypes array
  // since it contain all possible combinations
  // generated by carried forward rows and columns
  // by keeping only the remaining combinations after filtering
  // per row and column id
  const remainingRowIds = rows.map(({ answerId }) => answerId);
  const remainingColumnIds = columns.map(({ id }) => id);
  answerColumnValueTypes = answerColumnValueTypes.filter(
    ({ answerId, columnId }) =>
      includes(remainingRowIds, answerId) &&
      includes(remainingColumnIds, columnId),
  );

  const columnsGroupedByRows = groupBy(answerColumnValueTypes, 'answerId');
  let showRequiredInputSymbol = false;
  if (openEndedType !== QUESTION_MATRIX_OPEN_ENDED_CONSTANT_SUM) {
    showRequiredInputSymbol =
      findIndex(answerColumnValueTypes, ['required', true]) > -1;
  }

  const newRows = rows.map((answer) => {
    const { answerId, beforeLabel } = answer;
    const updatedRow = {
      id: answerId,
      label: beforeLabel,
    };
    const questionAnswer = find(question.answers, ['id', answerId]);
    updatedRow.optimizedImageUrl = get(
      questionAnswer,
      'optimized_image_url',
      '',
    );
    updatedRow.refId = get(questionAnswer, 'refId', '');
    updatedRow.imageUrl = get(questionAnswer, 'image_url', '');
    const updatedColumns = columns.filter(
      ({ id }) =>
        findIndex(columnsGroupedByRows[answerId], ['columnId', id]) !== -1,
    );
    updatedRow.columns = updatedColumns.map(
      ({ id, refId, beforeLabel: label }) => {
        const cell = answerColumnValueTypes.filter(
          (element) =>
            element.answerId === updatedRow.id && element.columnId === id,
        );
        return {
          id,
          refId,
          label,
          // there is only one element after filtering by row and column id
          required: cell[0] && cell[0].required,
        };
      },
    );
    return updatedRow;
  });

  const shuffledRows = shuffleArray(newRows, rowsShuffleType);

  // TODO: Check how we can alleviate this side effect
  Pollfish.store.shuffledAnswers = shuffledRows;

  return {
    rows: shuffledRows,
    openEndedType,
    openEndedStyle,
    openEndedInputSize,
    openEndedSignPlacement,
    openEndedSign,
    openEndedConstantSumType,
    min,
    max,
    decimals,
    total,
    showRequiredInputSymbol,
  };
}
